import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'

/**
 * Shared furniture for the board.
 *
 * The board is read at three distances — a laptop at the control desk, a
 * monitor by the door, a television across a hall — so type sizes are set per
 * screen rather than globally, and only the palette and the small pieces are
 * shared.
 */
export const T = {
  ink: '#16202b',
  muted: '#5c6b7f',
  faint: '#8b95a5',
  line: '#e4e9ef',
  ground: '#f6f8fa',
  card: '#ffffff',
  accent: '#a4262c',
  live: '#d9480f',
  liveBg: '#fdf0e6',
  done: '#1c6b3a',
  doneBg: '#eaf5ee',
  dark: '#0b1017',
  darkCard: '#141c26',
  darkLine: '#243141',
}

/** Re-runs a loader on a timer, and stops cleanly when the view goes away. */
export const usePoll = (loader, ms, deps = []) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const alive = useRef(true)
  const latest = useRef(null)
  const fn = useCallback(loader, deps)

  useEffect(() => {
    alive.current = true
    let timer

    // A change of scope — another city, another court — must clear the board
    // rather than leave the previous one showing under a new heading.
    latest.current = null
    setData(null)
    setError(null)

    const tick = async () => {
      try {
        const next = await fn()
        if (alive.current) {
          latest.current = next
          setData(next)
          setError(null)
        }
      } catch (e) {
        // A dropped poll keeps the last good board on screen rather than
        // blanking a television because one request timed out. Only a failure
        // with nothing already showing becomes an error.
        if (alive.current && !latest.current) {
          setError(e.response?.data?.message || 'Could not reach the scoring service.')
        }
      }
    }

    tick()
    if (ms) timer = setInterval(tick, ms)

    return () => {
      alive.current = false
      latest.current = null
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, ms])

  return { data, error }
}

/**
 * A knockout slot that is still waiting on an earlier result. Printing the
 * placeholder on both sides — "To be decided v To be decided" — says nothing;
 * one phrase says the same thing and reads as deliberate.
 */
export const matchName = (m) => {
  const tbd = (n) => !n || n === 'To be decided'
  if (tbd(m.home) && tbd(m.away)) return 'Winner of an earlier match'
  return `${seeded(m.home, m.home_seed)} v ${seeded(m.away, m.away_seed)}`
}

/**
 * An individual draw can run to hundreds of entrants, and in a company of this
 * size names repeat. The seed is what separates them, exactly as it does on a
 * printed draw sheet.
 */
export const seeded = (name, seed) => (seed ? `${name} [${seed}]` : name)

/** The venue string usually carries the city, so saying both repeats it. */
export const placeLine = (parts, city) => {
  const venue = parts.venue
  const out = [parts.label]
  if (venue && city && venue.includes(city)) out.push(venue)
  else out.push(city, venue)
  return out.filter(Boolean).join(' · ')
}

export const Pill = ({ status, children }) => {
  const look = {
    live: { bg: T.liveBg, fg: T.live },
    completed: { bg: T.doneBg, fg: T.done },
    walkover: { bg: T.doneBg, fg: T.done },
  }[status] || { bg: '#eef1f5', fg: T.muted }

  return (
    <span style={{
      padding: '2px 9px', borderRadius: 20, background: look.bg, color: look.fg,
      fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {children || (status === 'live' ? 'Live' : status)}
    </span>
  )
}

export const Chip = ({ on, children, ...rest }) => (
  <button {...rest} style={{
    padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
    border: `1px solid ${on ? T.accent : '#d9dee5'}`,
    background: on ? T.accent : '#fff', color: on ? '#fff' : '#42505f',
    fontWeight: on ? 700 : 500, font: 'inherit', fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  }}>{children}</button>
)

export const Empty = ({ children }) => (
  <p style={{ color: T.faint, fontSize: 13.5, margin: '10px 0' }}>{children}</p>
)

export const Stat = ({ value, label, tone }) => (
  <div style={{
    background: T.card, border: `1px solid ${T.line}`, borderRadius: 10, padding: '14px 16px',
  }}>
    <div style={{
      fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
      color: tone === 'live' ? T.live : T.ink,
    }}>{value}</div>
    <div style={{
      fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase',
      color: T.muted, fontWeight: 700, marginTop: 4,
    }}>{label}</div>
  </div>
)

/**
 * One match, as it appears in any list. The score sits between the two names so
 * the eye reads left-to-right the way a scoreboard is read.
 */
export const MatchRow = ({ m, onOpen, showCourt = true, action }) => (
  <div style={{
    background: T.card,
    border: `1px solid ${m.status === 'live' ? '#f0cdb4' : T.line}`,
    borderLeft: m.status === 'live' ? `3px solid ${T.live}` : `1px solid ${T.line}`,
    borderRadius: 10, padding: '11px 14px',
    display: 'flex', alignItems: 'center', gap: 14,
  }}>
    {/* The row opens the card; the action beside it goes somewhere else, so it
        sits outside the button rather than nested inside it. */}
    <button
      onClick={onOpen ? () => onOpen(m.id) : undefined}
      style={{
        flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent',
        border: 0, padding: 0, cursor: onOpen ? 'pointer' : 'default',
        font: 'inherit', fontFamily: 'inherit', color: T.ink,
      }}
    >
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center',
    }}>
      <span style={{ fontWeight: 700, textAlign: 'left', color: T.ink }}>{seeded(m.home, m.home_seed)}</span>
      <span style={{
        fontWeight: 800, color: m.status === 'live' ? T.live : T.accent,
        whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
      }}>
        {m.summary || m.start_time || '—'}
      </span>
      <span style={{ fontWeight: 700, textAlign: 'right', color: T.ink }}>{seeded(m.away, m.away_seed)}</span>
    </div>

    <div style={{
      fontSize: 12, color: T.muted, marginTop: 6, display: 'flex',
      gap: 8, alignItems: 'center', flexWrap: 'wrap',
    }}>
      {m.status === 'live' && <Pill status="live" />}
      <span>
        {[m.category, m.round, showCourt ? m.court : null, m.start_time && m.status !== 'live' ? null : m.day]
          .filter(Boolean).join(' · ')}
      </span>
      {m.note && <span style={{ color: T.live }}>{m.note}</span>}
      {m.winner && <span style={{ color: T.done }}>{m.winner} through</span>}
      </div>
    </button>

    {action}
  </div>
)

/**
 * Jump from a match to the screen for the court it is on. A coordinator
 * watching the desk is one click from putting that court on a television.
 */
export const ScreenButton = ({ city, sport, court, label = 'Screen' }) => {
  if (!city || !sport || !court) return null

  return (
    <Link
      to={`/board/screen/${encodeURIComponent(city)}/${sport}/${encodeURIComponent(court)}`}
      style={{
        flexShrink: 0, padding: '8px 15px', borderRadius: 8, textDecoration: 'none',
        border: `1px solid ${T.accent}`, color: T.accent, background: '#fff',
        fontWeight: 800, fontSize: 12.5, letterSpacing: '.04em', whiteSpace: 'nowrap',
      }}
    >{label} →</Link>
  )
}

export const SectionTitle = ({ children, aside }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: 12, margin: '0 0 10px', paddingBottom: 7, borderBottom: `2px solid ${T.line}`,
  }}>
    <h2 style={{ fontSize: 15.5, margin: 0, color: T.ink }}>{children}</h2>
    {aside && <span style={{ fontSize: 12, color: T.muted }}>{aside}</span>}
  </div>
)

export const page = {
  padding: 24, maxWidth: 1180, margin: '0 auto',
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", color: T.ink,
}
